import React, { useState, useRef, useEffect } from 'react';
import { API_BASE } from '../config';

function getCombatClass(text) {
  if (!text) return '';
  if (text.includes('CRIT!')) return 'combat-crit';
  if (text.includes('DEFEATED') || text.includes('have fallen')) return 'combat-kill';
  if (text.includes('HIT!') || text.includes('dmg')) return 'combat-hit';
  if (text.includes('MISS')) return 'combat-miss';
  if (text.includes('Healed') || text.includes('Long rest') || text.includes('Short rest')) return 'combat-heal';
  if (text.includes('turn')) return 'combat-turn';
  if (text.includes('Combat started') || text.includes('Combat ended')) return 'combat-phase';
  return '';
}

function renderCombatLine(text) {
  if (!text) return text;
  // Damage number pop
  let rendered = text.replace(/(\d+) damage/g, '<span class="dmg-pop">$1</span> damage');
  rendered = rendered.replace(/(\d+) dmg/g, '<span class="dmg-pop">$1</span> dmg');
  // HP bar inline
  rendered = rendered.replace(/HP: (\d+)\/(\d+)/g, (match, cur, max) => {
    const pct = Math.max(0, Math.min(100, (parseInt(cur) / parseInt(max)) * 100));
    const color = pct > 50 ? '#2ecc71' : pct > 25 ? '#f39c12' : '#e74c3c';
    return `HP: ${cur}/${max} <span class="inline-hp-bar"><span class="inline-hp-fill" style="width:${pct}%;background:${color}"></span></span>`;
  });
  // Crit emphasis — only add icon if not already present
  rendered = rendered.replace(/(?<!⚡ )CRIT!/g, '<span class="crit-text">⚡ CRIT!</span>');
  // Miss de-emphasis
  rendered = rendered.replace(/MISS/g, '<span class="miss-text">MISS</span>');
  // Kill emphasis
  rendered = rendered.replace(/DEFEATED!/g, '<span class="kill-text">💀 DEFEATED!</span>');
  // Icons — only add if not already present
  if (!rendered.includes('👹') && !rendered.includes('Enemy Phase')) {
    rendered = rendered.replace(/^(\[.*?\]) (.*?) attacks/g, '$1 👤 $2 attacks');
  }
  rendered = rendered.replace(/^(\[.*?\]) (.*?) casts/g, '$1 ✨ $2 casts');
  return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
}

export default function Console({ consoleLines, sendCommand, activeChannel, setActiveChannel, inputRef, pinnedChannels, onReorderPinned }) {
  const [input, setInput] = useState('');
  const [spellList, setSpellList] = useState([]);
  const [spellMatches, setSpellMatches] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const outputRef = useRef(null);

  // Load spell names once
  useEffect(() => {
    fetch('/spells.json').then(r => r.json()).then(data => setSpellList(data.map(s => s.name))).catch(() => {});
  }, []);

  // Autocomplete dictionary
  const COMMANDS = ['!f','!sp','!g','!q','!skip','!roster','!bestiary','!lr','!sr','!target','!cast','!dmheal','!dmhit','!give','!inflict','!buff','!removecondition','!godmode','!sync','!equip','!unequip','!inventory'];
  const ENEMIES = ['slappy','furniture','rug','shadow','geometry','echo','memory','remnant','angle','mimic','crawling claw','specter','gibbering mouther','poltergeist','will-o-wisp','nothic','intellect devourer','beholder','beholder zombie','wraith','banshee','flameskull','invisible stalker','the lonely','oblex','bodak','star spawn mangler','boneclaw','the grasp','the hallway','reflection','the furniture','threshold guardian','bilge keeper','the cook','drowned captain','depth horror','the measure'];

  // Spell dropdown matching
  useEffect(() => {
    const castMatch = input.match(/^!cast\s+(.+)$/i);
    if (castMatch && castMatch[1].length >= 2) {
      const partial = castMatch[1].toLowerCase();
      // Don't show dropdown if exact match already
      if (spellList.includes(partial)) { setSpellMatches([]); return; }
      const matches = spellList.filter(s => s.startsWith(partial)).slice(0, 6);
      setSpellMatches(matches);
      setSelectedIdx(0);
    } else {
      setSpellMatches([]);
    }
  }, [input, spellList]);

  const selectSpell = (spell) => {
    const cmd = `!cast ${spell}`;
    setInput(cmd);
    setSpellMatches([]);
    // Submit on next Enter (input is filled, dropdown gone)
  };

  const suggestion = React.useMemo(() => {
    if (!input || input.length < 2) return '';
    // If typing !sp <partial>, suggest enemy
    const spMatch = input.match(/^!sp\s+(.+)$/i);
    if (spMatch) {
      const partial = spMatch[1].toLowerCase();
      const match = ENEMIES.find(e => e.startsWith(partial));
      return match ? `!sp ${match}` : '';
    }
    // Suggest command
    const match = COMMANDS.find(c => c.startsWith(input.toLowerCase()));
    return match || '';
  }, [input]);

  useEffect(() => {
    if (outputRef.current) outputRef.current.scrollTop = outputRef.current.scrollHeight;
  }, [consoleLines]);

  const handleSubmit = () => {
    if (!input.trim()) return;
    sendCommand(input.trim(), activeChannel === 'auto' ? getAutoChannel(input.trim()) : activeChannel);
    setInput('');
  };

  const getAutoChannel = (cmd) => {
    const base = cmd.split(' ')[0].toLowerCase();
    const combatCmds = ['!f','!battle','!fight','!sp','!spawn','!g','!go','!n','!next','!q','!end','!target','!dmhit','!skip'];
    if (combatCmds.includes(base)) return 'combat';
    return 'dm-tools';
  };

  const channels = [...(pinnedChannels && pinnedChannels.length ? pinnedChannels : ['dm-tools','combat','general']), 'auto'];
  const [localOrder, setLocalOrder] = useState(null);
  const displayChannels = localOrder || channels;

  const [dragIdx, setDragIdx] = useState(null);

  const onPillDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; };
  const onPillDragOver = (e, idx) => { e.preventDefault(); if (dragIdx === null || dragIdx === idx) return; reorderPills(dragIdx, idx); setDragIdx(idx); };
  const onPillDragEnd = () => { setDragIdx(null); savePinnedOrder(); };

  const reorderPills = (from, to) => {
    const pills = (localOrder || channels).filter(c => c !== 'auto');
    const [moved] = pills.splice(from, 1);
    pills.splice(to, 0, moved);
    setLocalOrder([...pills, 'auto']);
  };

  const savePinnedOrder = () => {
    const pills = (localOrder || channels).filter(c => c !== 'auto');
    const session = localStorage.getItem('session');
    if (session) {
      try {
        const payload = JSON.parse(atob(session.split('.')[1]));
        fetch(`${API_BASE}/state/${payload.guild_id}`, {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + session, 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'set_pinned_channels', pinned_channels: pills })
        });
      } catch {}
    }
  };

  return (
    <div className="console">
      <div className="channel-bar">
        <span className="label">Channel:</span>
        {displayChannels.map((ch, idx) => (
          ch === 'auto' ? (
            <span key={ch} className={`channel-pill${activeChannel === ch ? ' active' : ''}`} onClick={() => setActiveChannel(ch)}>⚡ auto</span>
          ) : (
            <span
              key={ch}
              className={`channel-pill${activeChannel === ch ? ' active' : ''}${dragIdx === idx ? ' dragging' : ''}`}
              onClick={() => setActiveChannel(ch)}
              draggable
              onDragStart={e => onPillDragStart(e, idx)}
              onDragOver={e => onPillDragOver(e, idx)}
              onDragEnd={onPillDragEnd}
            >
              {ch}
            </span>
          )
        ))}
      </div>
      <div className="output" id="console-output" ref={outputRef}>
        {consoleLines.map((line, i) => (
          <div key={i} className={`line ${line.type || ''} ${getCombatClass(line.text)}`}>
            {renderCombatLine(line.text)}
          </div>
        ))}
      </div>
      <div className="input-area">
        {spellMatches.length > 0 && (
          <div className="spell-dropdown">
            {spellMatches.map((spell, i) => (
              <div key={spell} className={`spell-dropdown-item ${i === selectedIdx ? 'selected' : ''}`} onClick={() => selectSpell(spell)}>{spell}</div>
            ))}
          </div>
        )}
        <div className="input-wrapper">
          <input
            type="text"
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (spellMatches.length > 0) {
                if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, spellMatches.length - 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
                else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); selectSpell(spellMatches[selectedIdx]); }
                else if (e.key === 'Escape') { setSpellMatches([]); }
              } else {
                if (e.key === 'Enter') handleSubmit();
                else if (e.key === 'Tab' && suggestion) { e.preventDefault(); setInput(suggestion); }
              }
            }}
            placeholder="Enter command..."
          />
          {suggestion && suggestion !== input && <span className="autocomplete-ghost">{suggestion}</span>}
        </div>
        <button onClick={handleSubmit}>EXECUTE</button>
      </div>
    </div>
  );
}
